import type { Environment } from '~/engine/data/Environment';
import type { DatasetEnvironmentRegistry } from './DatasetEnvironmentRegistry';

/** The registry surface a window/level target is resolved from. */
export type WindowingRegistry = Pick<
    DatasetEnvironmentRegistry,
    'environmentFor' | 'listEntries'
>;

/**
 * The Environment a window/level write for a series applies through: the
 * series' own resident Environment, else a resident Environment that draws
 * it as its fused secondary.
 *
 * A pairing outlives the row it was made on: it stays on the entry so that
 * binding the image again brings it back. Only an entry a row holds now has
 * an Environment, so the first pairing found is not enough; the first one
 * with a resident Environment is.
 */
export function windowingEnvironmentFor(
    registry: WindowingRegistry,
    seriesUid: string
): Environment | undefined {
    const own = registry.environmentFor(seriesUid);
    if (own) {
        return own;
    }
    for (const entry of registry.listEntries()) {
        if (entry.secondaryImage?.seriesUid !== seriesUid) {
            continue;
        }
        const fusing = registry.environmentFor(entry.seriesUid);
        if (fusing) {
            return fusing;
        }
    }
    return undefined;
}

/**
 * Applies a window/level operation through the Environment
 * {@link windowingEnvironmentFor} resolves. A series no Environment holds,
 * or one whose Environment does not apply the operation, is surfaced with
 * a warning, never silently dropped.
 * @param apply Returns true when the environment hosted the series and
 * the operation was applied.
 * @returns true when the operation was applied.
 */
export function applyToWindowingTarget(
    registry: WindowingRegistry,
    seriesUid: string,
    apply: (environment: Environment) => boolean
): boolean {
    const environment = windowingEnvironmentFor(registry, seriesUid);
    if (environment && apply(environment)) {
        return true;
    }
    console.warn(
        'Window/level event for a series could not be resolved to a loaded image'
    );
    return false;
}
